import { Injectable, signal } from '@angular/core';

export interface CompanyOption {
  id: string;
  name: string;
}

export interface BranchOption {
  id: string;
  companyId: string;
  name: string;
}

export interface LocationOption {
  id: string;
  branchId: string;
  name: string;
}

export interface StoreOption {
  id: string;
  locationId: string;
  name: string;
}

export interface AccountOption {
  id: string;
  name: string;
  code: string;
}

export interface InvoiceSeriesOption {
  id: string;
  name: string;
  format: string;
}

export interface CustomerOption {
  id: string;
  name: string;
  phone?: string;
}

export interface WarehouseOption {
  id: string;
  name: string;
  code: string;
}

export interface StockLocationOption {
  id: string;
  name: string;
  warehouseId?: string;
}

export interface PriceListOption {
  id: string;
  name: string;
  discountRate?: number;
}

export interface TaxCategoryOption {
  id: string;
  name: string;
  rate: number;
}

export interface PaymentModeOption {
  id: string;
  name: string;
  type: string;
}

export interface CurrencyOption {
  code: string;
  name: string;
  symbol: string;
}

export interface DynamicSettingParameter {
  key: string;
  label: string;
  value: string;
}

export interface StoreSetting {
  id: string; // SettingID
  masterId: string;
  subscriptionId: string;
  company: string; // Company ID
  companyName: string;
  branchID: string; // Branch ID
  branchName: string;
  branchState?: string; // e.g. HYD, TX
  locationID: string; // Location ID
  locationName: string;
  store: string; // Store ID
  storeName: string;
  defaultCashAccount: string;
  cashSalesInvoice: string;
  defaultSalesAccount: string;
  defaultCustomer: string;
  defaultWarehouse: string;
  defaultStockLocation: string;
  defaultPriceList: string;
  defaultTaxCategory: string;
  defaultPaymentMode: string;
  defaultCurrency: string;
  // Header Binding Parameters for /api/LI210911101004_25EFCounterSalesInvoice/Insert
  passKey?: string;
  roleId?: string;
  salesAccountName?: string;
  stockControlAccountID?: string;
  stockControlAccountName?: string;
  salesCashAccountName?: string;
  counterOperatorID?: string;
  counterOperatorName?: string;
  isCreditSales?: boolean;
  isInvoiceRequest?: boolean;
  isEnableStorageBin?: boolean;
  isEnableTransportation?: boolean;
  isEnableOtherInfo?: boolean;
  isDiscountOnSummary?: boolean;
  defaultTaxTypeID?: string;
  defaultTaxTypeCode?: string;
  defaultTaxRateID?: string;
  defaultTaxRateValue?: number;
  defaultTaxRateCode?: string;
  defaultPaymentTerms?: string;
  shippingAddressId?: string;
  shippingLabelName?: string;
  shippingReceiverName?: string;
  billingAddressId?: string;
  billingLabelName?: string;
  billingReceiverName?: string;
  isActive: boolean;
  parameters: DynamicSettingParameter[];
  createdAt: string;
  updatedAt: string;
}

export interface ApiResponse<T> {
  success: boolean;
  status: number;
  message: string;
  data?: T;
  error?: string;
}

// Master Lookup Data
export const COMPANIES: CompanyOption[] = [
  { id: 'COMP-01', name: 'Acme Retail Enterprises Inc.' },
  { id: 'COMP-02', name: 'Global Hypermarket Corp.' },
  { id: 'COMP-03', name: 'Pacific Trendsetters LLC' }
];

export const BRANCHES: BranchOption[] = [
  // Under COMP-01
  { id: 'BR-TX01', companyId: 'COMP-01', name: 'Texas Central Hub' },
  { id: 'BR-CA01', companyId: 'COMP-01', name: 'California West Coast' },
  { id: 'BR-NY01', companyId: 'COMP-01', name: 'New York Metro District' },
  // Under COMP-02
  { id: 'BR-FL01', companyId: 'COMP-02', name: 'Florida South Gateway' },
  { id: 'BR-IL01', companyId: 'COMP-02', name: 'Illinois Midwest Division' },
  // Under COMP-03
  { id: 'BR-WA01', companyId: 'COMP-03', name: 'Pacific Northwest Region' }
];

export const LOCATIONS: LocationOption[] = [
  // Under BR-TX01
  { id: 'LOC-HOU', branchId: 'BR-TX01', name: 'Houston Downtown Logistics Center' },
  { id: 'LOC-AUS', branchId: 'BR-TX01', name: 'Austin Tech Corridor Center' },
  { id: 'LOC-DFW', branchId: 'BR-TX01', name: 'Dallas Fort Worth Galleria Hub' },
  // Under BR-CA01
  { id: 'LOC-LA', branchId: 'BR-CA01', name: 'Los Angeles Fashion District' },
  { id: 'LOC-SF', branchId: 'BR-CA01', name: 'San Francisco Financial Center' },
  // Under BR-NY01
  { id: 'LOC-MAN', branchId: 'BR-NY01', name: 'Manhattan 5th Ave Complex' },
  { id: 'LOC-BK', branchId: 'BR-NY01', name: 'Brooklyn Waterfront Terminal' },
  // Under BR-FL01
  { id: 'LOC-MIA', branchId: 'BR-FL01', name: 'Miami Biscayne Bay Center' },
  // Under BR-IL01
  { id: 'LOC-CHI', branchId: 'BR-IL01', name: 'Chicago Loop Flagship' },
  // Under BR-WA01
  { id: 'LOC-SEA', branchId: 'BR-WA01', name: 'Seattle Pike Place Logistics' }
];

export const STORES: StoreOption[] = [
  // Under LOC-HOU
  { id: 'store-01', locationId: 'LOC-HOU', name: 'Store 001 - Main St (HQ)' },
  { id: 'store-04', locationId: 'LOC-HOU', name: 'Store 004 - Market Square' },
  // Under LOC-AUS
  { id: 'store-02', locationId: 'LOC-AUS', name: 'Store 002 - West Mall' },
  { id: 'store-05', locationId: 'LOC-AUS', name: 'Store 005 - Domain Plaza' },
  // Under LOC-DFW
  { id: 'store-03', locationId: 'LOC-DFW', name: 'Store 003 - Airport Kiosk' },
  { id: 'store-06', locationId: 'LOC-DFW', name: 'Store 006 - Uptown Express' },
  // Under LOC-LA
  { id: 'store-07', locationId: 'LOC-LA', name: 'Store 007 - Rodeo Boutique' },
  // Under LOC-SF
  { id: 'store-08', locationId: 'LOC-SF', name: 'Store 008 - Market Street Kiosk' },
  // Under LOC-MAN
  { id: 'store-09', locationId: 'LOC-MAN', name: 'Store 009 - 5th Ave Flagship' },
  // Under LOC-BK
  { id: 'store-10', locationId: 'LOC-BK', name: 'Store 010 - DUMBO Outlet' }
];

export const CASH_ACCOUNTS: AccountOption[] = [
  { id: 'KOOP', code: 'KOOP', name: 'KOOP - Sales Cash Account (Counter)' },
  { id: 'ACC-1010', code: '1010', name: '1010 - Main Till Cash Register' },
  { id: 'ACC-1020', code: '1020', name: '1020 - Petty Cash Drawer' },
  { id: 'ACC-1030', code: '1030', name: '1030 - Store Vault Cash Account' },
  { id: 'ACC-1040', code: '1040', name: '1040 - Counter Register 1' },
  { id: 'ACC-1050', code: '1050', name: '1050 - Counter Register 2' }
];

export const SALES_INVOICE_SERIES: InvoiceSeriesOption[] = [
  { id: 'INV-CS-AUTO', name: 'Standard Counter Invoice (CS-YYYY-XXXX)', format: 'CS-{YYYY}-{SEQ}' },
  { id: 'INV-CS-RETAIL', name: 'Retail Fast Invoice (RCS-XXXX)', format: 'RCS-{SEQ}' },
  { id: 'INV-CS-TAX', name: 'Simplified Tax Invoice (STI-XXXX)', format: 'STI-{SEQ}' },
  { id: 'INV-CS-EXPRESS', name: 'Quick Counter Slip (QCS-XXXX)', format: 'QCS-{SEQ}' }
];

export const SALES_ACCOUNTS: AccountOption[] = [
  { id: 'Account', code: 'Account', name: 'Account - Counter Sales Revenue' },
  { id: 'ACC-4010', code: '4010', name: '4010 - Retail Sales Revenue' },
  { id: 'ACC-4020', code: '4020', name: '4020 - Over-the-Counter Sales' },
  { id: 'ACC-4030', code: '4030', name: '4030 - Direct Merchandise Revenue' },
  { id: 'ACC-4040', code: '4040', name: '4040 - Fast Moving Consumer Goods (FMCG)' }
];

export const DEFAULT_CUSTOMERS: CustomerOption[] = [
  { id: '006948b0-aef1-4447-854d-69892717f03f', name: 'NEW BHAGYALAXMI (P-171)', phone: 'PartyContact-05' },
  { id: 'CUST-WALKIN', name: 'Walk-in Customer (General Public)', phone: 'N/A' },
  { id: 'CUST-001', name: 'Karen Berg (VIP Club)', phone: '(555) 010-9988' },
  { id: 'CUST-002', name: 'James Smith (Regular Buyer)', phone: '(555) 012-3344' },
  { id: 'CUST-003', name: 'Maria Garcia (Regular Buyer)', phone: '(555) 015-5566' },
  { id: 'CUST-004', name: 'Robert Johnson (New Member)', phone: '(555) 018-7722' }
];

export const WAREHOUSES: WarehouseOption[] = [
  { id: 'WH-MAIN', code: 'MAIN', name: 'Main Central Fulfillment Warehouse' },
  { id: 'WH-STORE01', code: 'ST-01', name: 'Store 001 Onsite Stockroom' },
  { id: 'WH-STORE02', code: 'ST-02', name: 'Store 002 Stock Depot' },
  { id: 'WH-AIRPORT', code: 'AIR', name: 'Airport Kiosk Secure Vault' },
  { id: 'WH-REGIONAL', code: 'REG-TX', name: 'Texas Regional Distribution Hub' }
];

export const STOCK_LOCATIONS: StockLocationOption[] = [
  { id: 'LOC-SHELF-A', name: 'Front Display Shelves (A-Row)' },
  { id: 'LOC-SHELF-B', name: 'Checkout Gondola Display (B-Row)' },
  { id: 'LOC-BACKROOM', name: 'Backroom Fast-Pick Bay' },
  { id: 'LOC-RESERVE', name: 'Upper Racks High-Bay Reserve' },
  { id: 'HOUSTON-15', name: 'Houston Bin 15' },
  { id: 'AUSTIN-02', name: 'Austin Floor Bay 02' }
];

export const PRICE_LISTS: PriceListOption[] = [
  { id: 'PL-STANDARD', name: 'Standard Retail Price List', discountRate: 0 },
  { id: 'PL-WHOLESALE', name: 'Wholesale Tier 1 (15% Off)', discountRate: 15 },
  { id: 'PL-MEMBERS', name: 'Loyalty Club Member Pricing (10% Off)', discountRate: 10 },
  { id: 'PL-WEEKEND', name: 'Weekend Flash Promotional Rate', discountRate: 5 }
];

export const TAX_CATEGORIES: TaxCategoryOption[] = [
  { id: 'TAX-STD', name: 'Standard Sales Tax (6.25%)', rate: 0.0625 },
  { id: 'TAX-REDUCED', name: 'Reduced Food & Essentials Tax (2.5%)', rate: 0.025 },
  { id: 'TAX-ZERO', name: 'Zero-Rated Tax Exemption (0.00%)', rate: 0 },
  { id: 'TAX-STATE-LOCAL', name: 'Combined State + Local Tax (8.25%)', rate: 0.0825 }
];

export const PAYMENT_MODES: PaymentModeOption[] = [
  { id: 'Cash', name: 'Cash', type: 'cash' },
  { id: 'Card Payment', name: 'Card Payment (EMV / Contactless)', type: 'card' },
  { id: 'UPI / Split', name: 'UPI / Split QR Code', type: 'digital' },
  { id: 'Digital Wallet', name: 'Digital Wallet (Apple Pay / Google Pay)', type: 'wallet' }
];

export const CURRENCIES: CurrencyOption[] = [
  { code: 'USD', symbol: '$', name: 'USD ($ - US Dollar)' },
  { code: 'EUR', symbol: '€', name: 'EUR (€ - Euro)' },
  { code: 'GBP', symbol: '£', name: 'GBP (£ - British Pound)' },
  { code: 'CAD', symbol: 'CA$', name: 'CAD ($ - Canadian Dollar)' },
  { code: 'AUD', symbol: 'AU$', name: 'AUD ($ - Australian Dollar)' },
  { code: 'INR', symbol: '₹', name: 'INR (₹ - Indian Rupee)' }
];

const INITIAL_STORE_SETTINGS: StoreSetting[] = [
  {
    id: 'SET-001',
    masterId: 'MST-88201',
    subscriptionId: 'SUB-OCTO-2026',
    company: 'COMP-01',
    companyName: 'Acme Retail Enterprises Inc.',
    branchID: 'BR-TX01',
    branchName: 'Texas Central Hub',
    branchState: 'HYD',
    locationID: 'LOC-HOU',
    locationName: 'Houston Downtown Logistics Center',
    store: 'store-01',
    storeName: 'Store 001 - Main St (HQ)',
    defaultCashAccount: 'KOOP',
    cashSalesInvoice: 'INV-CS-AUTO',
    defaultSalesAccount: 'Account',
    defaultCustomer: '006948b0-aef1-4447-854d-69892717f03f',
    defaultWarehouse: 'WH-STORE01',
    defaultStockLocation: 'LOC-SHELF-A',
    defaultPriceList: 'PL-STANDARD',
    defaultTaxCategory: 'TAX-STD',
    defaultPaymentMode: 'Cash',
    defaultCurrency: 'USD',
    // Counter Sales Header Bindings
    passKey: 'PK-992014-X',
    roleId: 'ROLE-MGR-01',
    salesAccountName: 'Account',
    stockControlAccountID: 'Control',
    stockControlAccountName: 'name',
    salesCashAccountName: 'CashNAmeABC',
    counterOperatorID: 'operator-01',
    counterOperatorName: 'Operator-ABC',
    isCreditSales: true,
    isInvoiceRequest: true,
    isEnableStorageBin: false,
    isEnableTransportation: true,
    isEnableOtherInfo: true,
    isDiscountOnSummary: false,
    defaultTaxTypeID: 'gst',
    defaultTaxTypeCode: 'gst',
    defaultTaxRateID: 'taa-11',
    defaultTaxRateValue: 87.9,
    defaultTaxRateCode: 'ta-678',
    defaultPaymentTerms: 'assa',
    shippingAddressId: 'shipadd-12',
    shippingLabelName: 'shiplable-12',
    shippingReceiverName: 'shipRe-12',
    billingAddressId: 'billad-12',
    billingLabelName: 'billlab-12',
    billingReceiverName: 'billre-12',
    isActive: true,
    parameters: [
      { key: 'allow_negative_inventory', label: 'Allow Negative Inventory', value: 'false' },
      { key: 'auto_print_receipt', label: 'Auto Print Thermal Receipt', value: 'true' },
      { key: 'pos_counter_number', label: 'Terminal Counter ID', value: 'POS-TERM-01' },
      { key: 'cash_drawer_kick_pulse', label: 'Cash Drawer Kick Pulse (ms)', value: '150' }
    ],
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 7).toISOString(),
    updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString()
  }
];

@Injectable({
  providedIn: 'root'
})
export class StoreSettingsService {
  private readonly storageKey = 'pos_store_settings';
  
  // Reactive list of store settings
  storeSettings = signal<StoreSetting[]>([]);

  constructor() {
    this.initSettings();
  }

  private initSettings() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length > 0) {
          this.storeSettings.set(parsed);
          return;
        }
      }
    } catch (e) {
      console.error('Failed to read store settings from localStorage', e);
    }
    // Seed with initial setting
    this.storeSettings.set(INITIAL_STORE_SETTINGS);
    this.persistSettings(INITIAL_STORE_SETTINGS);
  }

  private persistSettings(settings: StoreSetting[]) {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(settings));
    } catch (e) {
      console.error('Failed to persist store settings', e);
    }
  }

  /**
   * Helper lookups
   */
  getCompanies(): CompanyOption[] {
    return COMPANIES;
  }

  getBranches(companyId?: string): BranchOption[] {
    if (!companyId) return BRANCHES;
    return BRANCHES.filter(b => b.companyId === companyId);
  }

  getLocations(branchId?: string): LocationOption[] {
    if (!branchId) return LOCATIONS;
    return LOCATIONS.filter(l => l.branchId === branchId);
  }

  getStores(locationId?: string): StoreOption[] {
    if (!locationId) return STORES;
    return STORES.filter(s => s.locationId === locationId);
  }

  /**
   * GET /api/store/settings
   * Retrieves all or filtered store settings.
   */
  async getStoreSettings(filters?: {
    subscriptionId?: string;
    company?: string;
    branch?: string;
    location?: string;
    store?: string;
  }): Promise<ApiResponse<StoreSetting[]>> {
    // Simulate brief network latency for realistic API behavior
    await new Promise(r => setTimeout(r, 60));

    let list = [...this.storeSettings()];
    if (filters) {
      if (filters.subscriptionId) {
        list = list.filter(s => s.subscriptionId.toLowerCase() === filters.subscriptionId?.toLowerCase());
      }
      if (filters.company) {
        list = list.filter(s => s.company === filters.company);
      }
      if (filters.branch) {
        list = list.filter(s => s.branchID === filters.branch);
      }
      if (filters.location) {
        list = list.filter(s => s.locationID === filters.location);
      }
      if (filters.store) {
        list = list.filter(s => s.store === filters.store);
      }
    }

    return {
      success: true,
      status: 200,
      message: 'Store settings retrieved successfully',
      data: list
    };
  }

  /**
   * GET /api/store/settings/:id
   */
  async getStoreSettingById(settingId: string): Promise<ApiResponse<StoreSetting>> {
    await new Promise(r => setTimeout(r, 40));
    const found = this.storeSettings().find(s => s.id === settingId);
    if (!found) {
      return {
        success: false,
        status: 404,
        message: `Store setting with ID ${settingId} not found`,
        error: 'NOT_FOUND'
      };
    }
    return {
      success: true,
      status: 200,
      message: 'Store setting retrieved successfully',
      data: { ...found }
    };
  }

  /**
   * POST /api/store/settings
   * Adds new store settings with duplicate check on:
   * Subscription + Company + Branch + Location + Store
   */
  async createStoreSetting(payload: Omit<StoreSetting, 'id' | 'createdAt' | 'updatedAt'> & { id?: string }): Promise<ApiResponse<StoreSetting>> {
    await new Promise(r => setTimeout(r, 100));

    // 1. Mandatory field validations
    const requiredFields: (keyof StoreSetting)[] = [
      'masterId', 'subscriptionId', 'company', 'branchID', 'branchName',
      'locationID', 'locationName', 'store', 'storeName',
      'defaultCashAccount', 'cashSalesInvoice', 'defaultSalesAccount',
      'defaultCustomer', 'defaultWarehouse', 'defaultStockLocation',
      'defaultPriceList', 'defaultTaxCategory', 'defaultPaymentMode',
      'defaultCurrency'
    ];

    for (const field of requiredFields) {
      const val = (payload as any)[field];
      if (val === undefined || val === null || (typeof val === 'string' && val.trim() === '')) {
        return {
          success: false,
          status: 400,
          message: `Field '${field}' is required and cannot be empty.`,
          error: 'VALIDATION_ERROR'
        };
      }
    }

    // 2. Duplicate Check: Subscription + Company + Branch + Location + Store
    const duplicate = this.storeSettings().find(s =>
      s.subscriptionId.trim().toLowerCase() === payload.subscriptionId.trim().toLowerCase() &&
      s.company === payload.company &&
      s.branchID === payload.branchID &&
      s.locationID === payload.locationID &&
      s.store === payload.store
    );

    if (duplicate) {
      return {
        success: false,
        status: 409,
        message: 'Store Settings already exist for this Subscription, Company, Branch, Location, and Store combination.',
        error: 'DUPLICATE_STORE_SETTINGS'
      };
    }

    // 3. Create new record
    const newId = payload.id?.trim() || ('SET-' + Math.floor(100 + Math.random() * 900));
    const now = new Date().toISOString();
    
    // Resolve Company Name
    const companyObj = COMPANIES.find(c => c.id === payload.company);
    const companyName = payload.companyName || companyObj?.name || payload.company;

    const newSetting: StoreSetting = {
      ...payload,
      id: newId,
      companyName,
      parameters: payload.parameters || [],
      createdAt: now,
      updatedAt: now
    };

    const updatedList = [newSetting, ...this.storeSettings()];
    this.storeSettings.set(updatedList);
    this.persistSettings(updatedList);

    return {
      success: true,
      status: 201,
      message: 'Store Settings created and saved successfully.',
      data: newSetting
    };
  }

  /**
   * PUT /api/store/settings/{SettingID}
   * Updates existing store settings by ID with duplicate check.
   */
  async updateStoreSetting(settingId: string, payload: Partial<StoreSetting>): Promise<ApiResponse<StoreSetting>> {
    await new Promise(r => setTimeout(r, 100));

    const existingIndex = this.storeSettings().findIndex(s => s.id === settingId);
    if (existingIndex === -1) {
      return {
        success: false,
        status: 404,
        message: `Store Settings with ID ${settingId} not found.`,
        error: 'NOT_FOUND'
      };
    }

    const current = this.storeSettings()[existingIndex];
    const candidateSub = payload.subscriptionId !== undefined ? payload.subscriptionId : current.subscriptionId;
    const candidateComp = payload.company !== undefined ? payload.company : current.company;
    const candidateBranch = payload.branchID !== undefined ? payload.branchID : current.branchID;
    const candidateLoc = payload.locationID !== undefined ? payload.locationID : current.locationID;
    const candidateStore = payload.store !== undefined ? payload.store : current.store;

    // Check duplicate against OTHER records
    const duplicate = this.storeSettings().find(s =>
      s.id !== settingId &&
      s.subscriptionId.trim().toLowerCase() === candidateSub.trim().toLowerCase() &&
      s.company === candidateComp &&
      s.branchID === candidateBranch &&
      s.locationID === candidateLoc &&
      s.store === candidateStore
    );

    if (duplicate) {
      return {
        success: false,
        status: 409,
        message: 'Store Settings already exist for this Subscription, Company, Branch, Location, and Store combination.',
        error: 'DUPLICATE_STORE_SETTINGS'
      };
    }

    // Resolve company name if changed
    let compName = payload.companyName || current.companyName;
    if (payload.company && payload.company !== current.company) {
      const c = COMPANIES.find(item => item.id === payload.company);
      if (c) compName = c.name;
    }

    const updatedSetting: StoreSetting = {
      ...current,
      ...payload,
      id: settingId,
      companyName: compName,
      updatedAt: new Date().toISOString()
    };

    const updatedList = [...this.storeSettings()];
    updatedList[existingIndex] = updatedSetting;
    this.storeSettings.set(updatedList);
    this.persistSettings(updatedList);

    return {
      success: true,
      status: 200,
      message: 'Store Settings updated successfully.',
      data: updatedSetting
    };
  }

  /**
   * DELETE /api/store/settings/{SettingID}
   */
  async deleteStoreSetting(settingId: string): Promise<ApiResponse<boolean>> {
    await new Promise(r => setTimeout(r, 60));
    const exists = this.storeSettings().some(s => s.id === settingId);
    if (!exists) {
      return {
        success: false,
        status: 404,
        message: `Store Settings with ID ${settingId} not found.`,
        error: 'NOT_FOUND'
      };
    }
    const filtered = this.storeSettings().filter(s => s.id !== settingId);
    this.storeSettings.set(filtered);
    this.persistSettings(filtered);
    return {
      success: true,
      status: 200,
      message: 'Store Settings deleted successfully.',
      data: true
    };
  }

  /**
   * Check if store settings are configured and active for a given store ID
   */
  getSettingForStore(storeId: string): StoreSetting | undefined {
    return this.storeSettings().find(s => s.store === storeId && s.isActive);
  }

  /**
   * Check if configured regardless of active status (for UI selection)
   */
  findAnySettingForStore(storeId: string): StoreSetting | undefined {
    return this.storeSettings().find(s => s.store === storeId);
  }
}
